import { Inject, Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import { loader } from '@daas-consulting/production/shared/frontend/store';
import {
  AuditService,
  SecurityAuditServiceId,
} from '@daas-consulting/security/audit/frontend/data_contracts';
import * as models from '@daas-consulting/security/audit/frontend/models';
import { AuditModel } from '@daas-consulting/security/audit/frontend/models';
import { Action, State, StateContext, ofActionDispatched } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { EMPTY, catchError, take, tap } from 'rxjs';
import * as actions from './audit-detail_preview.actions';
import * as consts from './audit-detail_preview.const';
import { AuditDetailPreviewStateModel } from './audit-detail_preview.state-model';

export const defaultFilter: models.Filter = {
  entity: null,
  entityId: null,
};

export const defaults: AuditDetailPreviewStateModel = {
  requestFilterModel: defaultFilter,
  activeFilterModel: defaultFilter,

  requestFilterCriteria: [],
  activeFilterCriteria: [],

  requestDetailFilterModel: undefined,

  requestItem: null,
  activeItem: null,

  items: [],
  requestHistoryFilterModel: undefined,

  activeDetail: undefined,
};

@State<AuditDetailPreviewStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class AuditDetailPreviewState {
  readonly #service: AuditService;
  readonly #eventHelper: store.EventHelper;

  constructor(
    @Inject(SecurityAuditServiceId) service: AuditService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.GetItem)
  getItem(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: actions.GetItem
  ): void {
    ctx.patchState({
      requestFilterModel: { ...payload },
    });
  }

  @Action(actions.GetItemRequest)
  getItemRequest(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    const state = ctx.getState();
    const entity: string = state?.requestFilterModel?.entity as string;
    const entityId: string = state?.requestFilterModel?.entityId as string;
    const conditionToCancelRequest = (x: typeof actions.GetItem): boolean => {
      const action = x as unknown as actions.GetItem;
      const rv =
        action.payload.entity !== entity ||
        action.payload.entityId !== entityId;
      return rv;
    };
    this.#service
      .get$(entity, entityId)
      .pipe(
        // Take a single result and disconnect the subscription.
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetItem,
          conditionToCancelRequest,
          () => {
            const action = new loader.actions.HideLoading();
            ctx.dispatch(action);
          }
        ),
        tap((item) => {
          const action = new actions.GetItemSuccessful(item as AuditModel);
          ctx.dispatch(action);
        }),
        catchError((error) => {
          const action = new actions.GetItemFailure(error as Error);
          ctx.dispatch(action);
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetItemSuccessful)
  getItemSuccessful(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: actions.GetItemSuccessful
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      activeFilterModel: state.requestFilterModel,
      activeItem: payload,
    });
  }

  @Action(actions.GetHistory)
  getHistory(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: InstanceType<typeof actions.GetHistory>
  ): void {
    ctx.setState(
      patch({
        requestHistoryFilterModel: {
          entity: payload?.entity as string,
          entityId: payload?.entityId as string,
        },
      })
    );
  }

  @Action(actions.GetHistoryRequest)
  getHistoryRequest(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    const state = ctx.getState();
    const entity: string = state?.requestHistoryFilterModel?.entity as string;
    const entityId: string = state?.requestHistoryFilterModel
      ?.entityId as string;
    const conditionToCancelRequest = (
      x: typeof actions.GetHistory
    ): boolean => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const action = x as any;
      const rv =
        action?.payload?.entity !== entity ||
        action?.payload?.entityId !== entityId;
      return rv;
    };
    this.#service
      .history$(entity, entityId)
      .pipe(
        // Take a single result and disconnect the subscription.
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetHistory,
          conditionToCancelRequest
        ),
        tap((item) => {
          const action = new actions.GetHistorySuccessful(item as AuditModel[]);
          ctx.dispatch(action);
        }),
        catchError((error) => {
          const action = new actions.GetHistoryFailure(error as Error);
          ctx.dispatch(action);
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetHistorySuccessful)
  getHistorySuccessful(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: InstanceType<typeof actions.GetHistorySuccessful>
  ): void {
    const state = ctx.getState();
    ctx.setState(
      patch({
        items: payload,
        activeHistoryFilterModel: {
          entity: state.requestHistoryFilterModel?.entity as string,
          entityId: state.requestHistoryFilterModel?.entityId as string,
        },
        requestHistoryFilterModel: undefined,
      })
    );
  }

  @Action(actions.GetHistoryFailure)
  getHistoryFailure(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    ctx.setState(
      patch({
        items: [],
        activeHistoryFilterModel: undefined,
        requestHistoryFilterModel: undefined,
      })
    );
  }

  @Action(actions.GetDetail)
  getDetail(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: InstanceType<typeof actions.GetDetail>
  ): void {
    ctx.setState(
      patch({
        requestDetailFilterModel: {
          entity: payload?.entity as string,
          entityId: payload?.entityId as string,
          seq: payload?.seq as number,
        },
      })
    );
  }

  @Action(actions.GetDetailRequest)
  getDetailRequest(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    const state = ctx.getState();
    const entity: string = state?.requestDetailFilterModel?.entity as string;
    const entityId: string = state?.requestDetailFilterModel
      ?.entityId as string;
    const seq: number = state?.requestDetailFilterModel?.seq as number;
    const conditionToCancelRequest = (x: typeof actions.GetDetail): boolean => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const action = x as any;
      const rv =
        action?.payload?.entity !== entity ||
        action?.payload?.entityId !== entityId ||
        action?.payload?.seq !== seq;
      return rv;
    };
    this.#service
      .detail$(entity, entityId, seq)
      .pipe(
        // Take a single result and disconnect the subscription.
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetDetail,
          conditionToCancelRequest
        ),
        tap((item) => {
          const action = new actions.GetDetailSuccessful(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            item as any
          );
          ctx.dispatch(action);
        }),
        catchError((error) => {
          const action = new actions.GetDetailFailure(error as Error);
          ctx.dispatch(action);
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetDetailSuccessful)
  getDetailSuccessful(
    ctx: StateContext<AuditDetailPreviewStateModel>,
    { payload }: InstanceType<typeof actions.GetDetailSuccessful>
  ): void {
    const state = ctx.getState();
    ctx.setState(
      patch({
        activeDetail: payload,
        activeDetailFilterModel: {
          entity: state.requestDetailFilterModel?.entity as string,
          entityId: state.requestDetailFilterModel?.entityId as string,
          seq: state.requestDetailFilterModel?.seq as number,
        },
        requestDetailFilterModel: undefined,
      })
    );
  }

  @Action(actions.GetDetailFailure)
  getDetailFailure(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    ctx.setState(
      patch({
        activeDetail: undefined,
        activeDetailFilterModel: undefined,
        requestDetailFilterModel: undefined,
      })
    );
  }

  @Action(actions.Reset)
  reset(ctx: StateContext<AuditDetailPreviewStateModel>): void {
    ctx.setState(
      patch({
        ...defaults,
      })
    );
  }

  private _connectEvents(): void {
    this._connectEventsGetItem();
    this._connectEventsGetHistory();
    this._connectEventsGetDetail();
  }

  private _connectEventsGetItem() {
    this.#eventHelper.connectEvents(
      actions.GetItem,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(actions.GetItem, actions.GetItemRequest);
    this.#eventHelper.connectEvents(
      actions.GetItemSuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.GetItemFailure,
      loader.actions.HideLoading
    );
  }

  private _connectEventsGetHistory() {
    this.#eventHelper.connectEvents(
      actions.GetHistory,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.GetHistory,
      actions.GetHistoryRequest
    );
    this.#eventHelper.connectEvents(
      actions.GetHistorySuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.GetHistoryFailure,
      loader.actions.HideLoading
    );
  }

  private _connectEventsGetDetail() {
    this.#eventHelper.connectEvents(
      actions.GetDetail,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.GetDetail,
      actions.GetDetailRequest
    );
    this.#eventHelper.connectEvents(
      actions.GetDetailSuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.GetDetailFailure,
      loader.actions.HideLoading
    );
  }
}
